<script setup lang="ts">
import { ref, toRefs, watch, computed } from 'vue';
import { EMIT } from '@/enum';

interface ReferralInterface {
  id: number;
  service_provider_id: number | null;
  index: number;
  student_name: string;
  page: number;
}

interface PropsInterface {
  referrals: ReferralInterface[];
  open: boolean;
}

const emit = defineEmits([EMIT.CANCEL, EMIT.SAVE, 'update-referrals']);

const props = defineProps<PropsInterface>();

const { referrals, open } = toRefs(props);

const referralsArray = ref<ReferralInterface[]>([...referrals.value]);

watch(referrals, (newReferrals) => {
  referralsArray.value = [...newReferrals];
});

// Computed properties to filter users
const usersWithServiceProvider = computed(() =>
  referralsArray.value.filter((referral) => referral.service_provider_id !== null),
);

const usersWithoutServiceProvider = computed(() =>
  referralsArray.value.filter((referral) => referral.service_provider_id === null),
);

const onCancel = () => emit(EMIT.CANCEL);

const onApprove = () => emit(EMIT.SAVE);

const onDelete = (index: number, listType: 'with' | 'without') => {
  if (listType === 'with') {
    const referral = usersWithServiceProvider.value[index];
    const referralIndex = referralsArray.value.findIndex((r) => r.id === referral.id);
    referralsArray.value.splice(referralIndex, 1);
  } else {
    const referral = usersWithoutServiceProvider.value[index];
    const referralIndex = referralsArray.value.findIndex((r) => r.id === referral.id);
    referralsArray.value.splice(referralIndex, 1);
  }
  emit('update-referrals', referralsArray.value);
};
</script>

<template>
  <div>
    <ConfirmAction v-if="open" header="Approve Referral" accept-label="Approve" @accept="onApprove" @cancel="onCancel">
      <div class="flex text-gray-800 align-items-center">
        <Icon name="warning" class="confirm-dialog-icon text-primary-800" />
        <span class="ml-3">
          Are you sure you want to approve the following referrals?

          <!-- Users with Service Provider -->
          <div v-if="usersWithServiceProvider.length" class="section">
            <div class="name-grid">
              <div v-for="(referral, index) in usersWithServiceProvider" :key="referral.id" class="name-item">
                <span>{{ referral.student_name }}</span>
                <button class="delete-button ml-1" @click="onDelete(index, 'with')">
                  <span class="material-icons text-red-600">highlight_off</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Users without Service Provider -->
          <div v-if="usersWithoutServiceProvider.length" class="section">
            <p>These referrals require a Service Provider to be approved.</p>
            <div class="name-grid">
              <div v-for="referral in usersWithoutServiceProvider" :key="referral.id" class="name-item">
                <span>{{ referral.student_name }} (Page {{ referral.page }})</span>
              </div>
            </div>
          </div>
        </span>
      </div>
    </ConfirmAction>
  </div>
</template>

<style scoped lang="scss">
.confirm-action {
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;

  .p-dialog-header,
  .p-dialog-content {
    background: white !important;
  }

  .p-dialog-header {
    padding: 20px !important;

    span {
      font-size: 1.25rem !important;
    }
  }

  .p-dialog-content {
    padding: 20px !important;

    .text-red-600 {
      color: $red !important;
    }
  }

  .p-dialog-footer {
    padding: 15px !important;
  }

  .section {
    margin-top: 20px;

    h3 {
      font-size: 1.125rem;
      font-weight: 600;
      margin-bottom: 10px;
    }
  }

  .name-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 10px;
  }

  .name-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    line-height: 1.5;
  }

  .delete-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
  }
}
</style>
